import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { AppSuiteSearchableDropDown } from 'common/components/AppSuiteSearchableDropDown';
import { Page } from 'common/components/Page';
import { StyledBreadcrumbs } from 'common/components/StyledBreadcrumbs';
import { TenantSearchableDropDown } from 'common/components/TenantSearchableDropDown';
import { ACL_RESOURCE_USER_LIST_REPORT_SLUG, ACL_RESOURCE_USER_LIST_REPORT_TITLE, DEFAULT_TENANT } from 'common/constants/reports';
import { Routes } from 'common/constants/routes';
import { AclResource, ApplicationSuite, AppSuiteTenant } from 'common/types/Entities';
import { useProcessFetchAclResourcesUserListReportData } from 'features/reports/hooks/aclResourceUserListReportHooks/useProcessFetchAclResourcesUserListReportData';
import { useGenerateDownloadableCSV } from 'features/reports/hooks/common/useGenerateDownloadableCSV';
import { selectIsTenantDropDownDisabled, setTenantDropDownDisabled } from 'features/reports/tenantSearchableDropDownSlice';
import { useSnackbar } from 'notistack';

import { AclMultipleSelectDropDown } from '../common/AclMultipleSelectDropDown';
import { CsvReportExportPopupLoader } from '../common/CsvReportExportPopupLoader';

export function AclResourceUserListReport() {

    const reportFields = [
        'userId',
        'firstName',
        'lastName',
        'email',
        'licenseBillingRecord',
        'aclResourceDisplayName',
        'aclResourceName',
        'userGroupName',
        'aclStatus',
        'userStatus',
        'licenseStartDate',
        'licenseEndDate',
        'aclStartDate',
        'aclEndDate',
        'userTenantMembershipStartDate',
        'userTenantMembershipEndDate',
    ];
    const DEFAULT_POPUP_LOADER_MESSAGE = 'Exporting...';
    const ACL_RESOURCE_USER_LIST_REPORT_FILENAME = 'UsersAclReport';
    const ACL_RESOURCE_USER_LIST_REPORT_ERROR_MESSAGE_PREFIX = 'There was an error while generating ACL Resource - User List Report:';

    const [aclResources, setAclResources] = useState<AclResource[]>([]);
    const [appSuite, setAppSuite] = useState<ApplicationSuite | null>(null);
    const [tenant, setTenant] = useState<AppSuiteTenant | null>(null);
    const [includeInactiveUsers, setIncludeInactiveUsers] = useState(false);

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { convertToCSVBasedOnModel, downloadCSV } = useGenerateDownloadableCSV(ACL_RESOURCE_USER_LIST_REPORT_FILENAME);

    const isTenantSearchableDropDownDisabled = useAppSelector(selectIsTenantDropDownDisabled);
    const disableGenerateButton = !appSuite || !tenant?.id;

    useEffect(() => {
        if(appSuite?.id) {
            dispatch(setTenantDropDownDisabled(false));
            setTenant(DEFAULT_TENANT);
            setAclResources([]);
        }
    }, [appSuite?.id]);

    useEffect(() => {
        dispatch(setTenantDropDownDisabled(true));
    }, []);

    const handleApplicationSuiteChange = (_e: unknown, newAppSuite: ApplicationSuite | null) => {
        setAppSuite(newAppSuite);
    };

    const handleTenantChange = (_e: unknown, newTenant: AppSuiteTenant | null) => {
        setTenant(newTenant);
        setAclResources([]);
    };

    const handleIncludeInactiveUsers = (_e: unknown, includeInactiveUsers: boolean) => {
        setIncludeInactiveUsers(includeInactiveUsers);
    };

    const {
        processing,
        processFetchAclResourcesUserListReportData
    } = useProcessFetchAclResourcesUserListReportData(tenant?.id, {
        includeInactive: includeInactiveUsers,
        resourcesIds: aclResources.map(resource => resource.id)
    });

    const handleClickCancel = () => {
        navigate(`/${Routes.Reports}`);
    };

    const handleGenerateReport = async () => {
        try {
            const reportData = (await processFetchAclResourcesUserListReportData()) ?? [];
            const csv = await convertToCSVBasedOnModel(reportData, reportFields);
            downloadCSV(csv);
        } catch (error: unknown) {
            const err = error as Error;
            enqueueSnackbar(
                `${ACL_RESOURCE_USER_LIST_REPORT_ERROR_MESSAGE_PREFIX} ${err.message}`,
                { variant: 'error' }
            );
        }
    };

    return (
        <Page
            title={`${ACL_RESOURCE_USER_LIST_REPORT_TITLE} Report`}
            header={(
                <Typography variant='h5' gutterBottom component='div'>
                    <StyledBreadcrumbs
                        numberOfTabsToExcludeFromLinks={1}
                        guidNameMappings={[{
                            resourceName: ACL_RESOURCE_USER_LIST_REPORT_TITLE,
                            resourceId: ACL_RESOURCE_USER_LIST_REPORT_SLUG
                        }]}
                    />
                    {`${ACL_RESOURCE_USER_LIST_REPORT_TITLE} Report`}
                </Typography>
            )}
        >

            <Stack
                sx={{ paddingTop: '1rem', width: '30%' }}
                spacing={2}
            >
                <AppSuiteSearchableDropDown
                    value={appSuite}
                    onChange={handleApplicationSuiteChange}
                />
                <TenantSearchableDropDown
                    appSuite={appSuite}
                    value={tenant ?? DEFAULT_TENANT}
                    onChange={handleTenantChange}
                    disabled={isTenantSearchableDropDownDisabled}
                />
                <AclMultipleSelectDropDown
                    value={aclResources}
                    onChange={setAclResources}
                    tenantId={tenant?.id ?? ''}
                />
                <Typography
                    variant="caption"
                    sx={{
                        color: 'text.secondary',
                        fontStyle: 'italic',
                        marginTop: '-8px',
                        marginBottom: '8px'
                    }}
                >
                    {aclResources.length === 0
                        ? '⚠️ No ACL resources selected - all ACL resources will be included by default'
                        : `${aclResources.length} ACL resource(s) selected`
                    }
                </Typography>

                <FormControlLabel
                    control={<Checkbox
                        value={includeInactiveUsers}
                        onChange={handleIncludeInactiveUsers}
                    />}
                    label={
                        <Typography>
                            Include Inactive Users/ACLs?
                        </Typography>
                    }
                />

                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Button
                        variant={'contained'}
                        sx={{ marginRight: '1rem' }}
                        onClick={handleGenerateReport}
                        disabled={disableGenerateButton}
                    >
                        Generate
                    </Button>
                    <Button
                        variant={'outlined'}
                        onClick={handleClickCancel}
                    >
                        Cancel
                    </Button>
                </div>
            </Stack>
            <CsvReportExportPopupLoader
                open={processing}
                title={DEFAULT_POPUP_LOADER_MESSAGE}
            />
        </Page>
    );
}
